// WP-07 — RBAC route guards (LLD-05 §4/§11): RequireAuth (session present)
// composed with RequireRole (allowed Role[]). UI RBAC is CONVENIENCE ONLY —
// the Core API enforces every rule server-side; a tampered client still gets
// 403 (LLD-05 §8). Foremen never hold an office session (BR-021): a persisted
// FOREMAN-role session (tampered/stale storage) is cleared on sight.
import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { isOfficeRole, type Role } from "../auth/roles";
import { useSession } from "../auth/session-store";

export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const tokens = useSession((s) => s.tokens);
  const role = useSession((s) => s.role);
  const clearSession = useSession((s) => s.clearSession);

  const nonOfficeRole = Boolean(tokens && role && !isOfficeRole(role));
  useEffect(() => {
    if (nonOfficeRole) clearSession();
  }, [nonOfficeRole, clearSession]);

  if (nonOfficeRole) {
    return <Navigate to="/sign-in" replace />;
  }
  if (!tokens || !role) {
    return (
      <Navigate to="/sign-in" state={{ from: location.pathname }} replace />
    );
  }
  return <>{children}</>;
}

/**
 * Role gate for a single route. A signed-in user without the grant is
 * redirected to the landing screen — the screen is hidden from their nav
 * (UX v1.4 §2 legend), so a direct URL is the only way here.
 */
export function RequireRole({
  allowed,
  children,
}: {
  allowed: readonly Role[];
  children: ReactNode;
}) {
  const role = useSession((s) => s.role);
  if (!role || !allowed.includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
